/**
 * Shared formatting helpers for the Tax Reporting views.
 *
 * Kept deliberately small. Each helper accepts the "wire" type (string
 * decimals from the API or null/undefined for missing values) and returns a
 * display string. Views should import these instead of re-implementing
 * Intl.NumberFormat / Date parsing logic per file.
 */

const USD_FORMATTER = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const CRYPTO_FORMATTER = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 8,
});

/**
 * Format a USD value (string from the API, possibly null) as "$1,234.56".
 * Returns "—" for null/undefined/empty.
 */
export function formatUsd(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (Number.isNaN(num)) return '—';
  return USD_FORMATTER.format(num);
}

/**
 * Format a crypto quantity (string from the API) with up to 8 decimal places.
 * Returns "—" for null/undefined/empty.
 */
export function formatCrypto(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (Number.isNaN(num)) return '—';
  return CRYPTO_FORMATTER.format(num);
}

/**
 * Format an ISO timestamp as `YYYY-MM-DD HH:MM` (UTC).
 * Returns "—" for null/undefined/empty.
 */
export function formatTimestamp(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const pad = (n: number) => n.toString().padStart(2, '0');
  return (
    `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ` +
    `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`
  );
}

/**
 * Format an ISO date as `YYYY-MM-DD` (UTC).
 * Returns "—" for null/undefined/empty.
 */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}

/**
 * Truncate a long order ID to its first 8 chars + ellipsis.
 * Strings ≤ 8 chars are returned unchanged. Returns "—" for null/undefined/empty.
 */
export function formatOrderId(id: string | null | undefined): string {
  if (!id) return '—';
  return id.length > 8 ? `${id.substring(0, 8)}…` : id;
}

/**
 * Format an elapsed duration between two ISO timestamps as compact runtime
 * (e.g. "42s", "3m 12s", "1h 24m"). Returns "—" when either input is missing
 * or unparseable, "(running)" when finishedAt is absent but startedAt is set
 * — caller should pass null for finishedAt on in-progress runs.
 */
export function formatRuntime(
  startedIso: string | null | undefined,
  finishedIso: string | null | undefined,
  nowMs?: number
): string {
  if (!startedIso) return '—';
  const start = new Date(startedIso);
  if (Number.isNaN(start.getTime())) return '—';
  let endMs: number;
  if (finishedIso) {
    const end = new Date(finishedIso);
    if (Number.isNaN(end.getTime())) return '—';
    endMs = end.getTime();
  } else {
    // Run still in progress — measure against caller-provided "now" so the
    // value re-evaluates on the global auto-refresh cadence (no separate
    // setInterval). If caller didn't pass nowMs, fall back to Date.now()
    // for callers that just want a one-shot snapshot.
    endMs = nowMs ?? Date.now();
  }
  let secs = Math.max(0, Math.round((endMs - start.getTime()) / 1000));
  if (secs < 60) return `${secs}s`;
  const mins = Math.floor(secs / 60);
  secs = secs % 60;
  if (mins < 60) return secs === 0 ? `${mins}m` : `${mins}m ${secs}s`;
  const hours = Math.floor(mins / 60);
  const remMins = mins % 60;
  return remMins === 0 ? `${hours}h` : `${hours}h ${remMins}m`;
}
